import { EntityState } from '@reduxjs/toolkit';

import { Roles } from '../../AddProject/types/types';

export interface SortedUser {
    id: number;
    email: string;
    name: null | string;
    avatar: null | string;
}
export interface ProjectUser extends SortedUser {
    role: Roles;
}

export interface DashboardSortedUser {
    rest_of_team_count: number;
    users: SortedUser[];
}

export interface ProjectUsers {
    manager: EntityState<ProjectUser>;
    foreman: EntityState<ProjectUser>;
    purchase_agent: EntityState<ProjectUser>;
}

export interface Information {
    name: string;
    address: string;
    contract_number: string;
    contract_date: string;
    latitude: number;
    longitude: number;
    description: string;
    is_archive: boolean;
    customers: EntityState<CustomerInfo>;
    legal_entities: LegalEntity[];
    contactPersons: EntityState<ContactPerson>;
    projectUsers: {
        sorted_users: DashboardSortedUser;
        project_users: ProjectUsers;
    };
}

export interface Weather {
    current_date: string;
    current_temperature: string;
    current_icon_image_url: string;
    latitude: number;
    longitude: number;
    days: Days[];
    map_url: string;
}

export interface Days {
    date: string;
    temperature: string;
    icon_image_url: string;
}
export interface Documentation {
    currentDocument: null | string;
    is_having_contract_document: boolean;
    is_having_proposal_document: boolean;
    tariff_access: TariffAccess;
}
export interface Visualization {
    id: number;
    original_url: string;
    preview_url: string;
}
export interface FinancialIndicators {
    current_period_customer_expenses: number;
    current_period_expenses: number;
    upcoming_period_customer_expenses: number;
    upcoming_period_expenses: number;
    currency: CurrencyInfo;
    key_metrics: KeyMetrics;
    current_period_customer_expenses_percent: number;
    current_period_expenses_percent: number;
    upcoming_period_customer_expenses_percent: number;
    upcoming_period_expenses_percent: number;
}
export interface Estimates {
    in_draft_estimates_cost: number;
    in_draft_estimates_count: number;
    in_exploitations_estimates_cost: number;
    in_exploitations_estimates_count: number;
    in_exploitations_estimates_done_percent: number;
    estimates: DashboardEstimate[];
    currency: CurrencyInfo;
}
export interface ConstructionProgress {
    date?: string;
    description?: string;
    id: number;
    original_url?: string;
    preview_url?: string;
    albums: {
        value: string;
        title: string;
    }[];
}

export interface CustomerInfo {
    id: number;
    name: string;
    guid?: string;
    selected?: boolean;
}

export interface LegalEntity {
    id: number;
    guid: string;
    name: string;
    selected: boolean;
    locked_by_tariff: boolean;
}

export interface ContactPerson {
    id: number;
    name: string;
    guid?: string;
    preview_url: string;
}

export interface ManagerInformation {
    full_name: string;
    position: string;
    email: string;
    phone: string;
    preview_url: null | string;
}

export interface Customer {
    name: string;
    email: string;
    phone: string;
    description: string;
    full_name: string;
    legal_address: string;
    passport: string;
    inn: string;
    kpp: string;
    ogrn: string;
    ogrnip: string;
    okpo: string;
    bank: string;
    bik: string;
    correspondent_account_number: string;
    current_account_number: string;
    contact_name: string;
    actual_address: string;
    contact_comment: string;
}

export interface SetCustomerBody {
    guid: string;
}

export interface CurrencyInfo {
    code: string;
    id: number;
    short_code: string;
    sign: string;
    title: string;
}

export interface KeyMetrics {
    customer_debt: number;
    budget_saving: number;
    project_balance: number;
    profit_forecast_value: number;
    profit_forecast_percent: number;
    planned_profit_value: number;
    planned_profit_percent: number;
}

export enum EstimatesStatus {
    DRAFT = 'draft',
    ON_AGREEMENT = 'on_agreement',
    IN_EXPLOITATIONS = 'in_exploitations',
    COMPLETED = 'completed',
}

export interface DashboardEstimate {
    id: number;
    name: string;
    status: EstimatesStatus;
    done_percent: number;
    created_at: string;
}

export interface TariffAccess {
    doc_act: boolean;
    doc_contract: boolean;
    doc_kp: boolean;
    doc_order: boolean;
    doc_reconciliation: boolean;
    doc_your_contract: boolean;
    doc_your_proposal: boolean;
}
